import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CategoryFilter = ({ onCategoryChange, onSearchChange }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    {
      id: 'all',
      name: 'All Posts',
      icon: 'Grid3X3',
      count: 47,
      color: 'text-foreground'
    },
    {
      id: 'technical',
      name: 'Technical Tutorials',
      icon: 'Code',
      count: 18,
      color: 'text-accent'
    },
    {
      id: 'career',
      name: 'Career Growth',
      icon: 'TrendingUp',
      count: 15,
      color: 'text-success'
    },
    {
      id: 'community',
      name: 'Community Building',
      icon: 'Users',
      count: 12,
      color: 'text-warning'
    },
    {
      id: 'insights',
      name: 'Industry Insights',
      icon: 'Lightbulb',
      count: 8,
      color: 'text-error'
    }
  ];

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    onCategoryChange(categoryId);
  };

  const handleSearchChange = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    onSearchChange(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearchChange('');
  };

  return (
    <section className="bg-background py-12 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Icon name="Search" size={20} className="text-text-secondary" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search articles, tutorials, and insights..."
                className="w-full pl-12 pr-12 py-4 bg-card border border-border rounded-xl text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-brand"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-secondary hover:text-foreground transition-brand"
                >
                  <Icon name="X" size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => handleCategoryClick(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-brand-medium transition-brand ${
                  activeCategory === category?.id
                    ? 'bg-primary/10 text-primary border border-primary/20' :'bg-card text-text-primary border border-border hover:bg-primary/5 hover:text-primary hover:border-primary/20'
                }`}
              >
                <Icon 
                  name={category?.icon} 
                  size={16} 
                  className={activeCategory === category?.id ? 'text-primary' : category?.color}
                />
                <span>{category?.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeCategory === category?.id
                    ? 'bg-primary/20 text-primary' :'bg-muted text-text-secondary'
                }`}>
                  {category?.count}
                </span>
              </button>
            ))}
          </div>

          {/* Filter Options */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-text-secondary font-brand-medium">Sort by:</span>
              <select className="px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-brand">
                <option value="latest">Latest</option>
                <option value="popular">Most Popular</option>
                <option value="trending">Trending</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-text-secondary font-brand-medium">Difficulty:</span>
              <div className="flex items-center space-x-2">
                <button className="px-3 py-1 rounded-full text-xs font-brand-medium bg-success/10 text-success border border-success/20 hover:bg-success/20 transition-brand">
                  Beginner
                </button>
                <button className="px-3 py-1 rounded-full text-xs font-brand-medium bg-warning/10 text-warning border border-warning/20 hover:bg-warning/20 transition-brand">
                  Intermediate
                </button>
                <button className="px-3 py-1 rounded-full text-xs font-brand-medium bg-error/10 text-error border border-error/20 hover:bg-error/20 transition-brand">
                  Advanced
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {(activeCategory !== 'all' || searchQuery) && (
            <div className="flex items-center space-x-4 pt-4 border-t border-border">
              <span className="text-sm text-text-secondary font-brand-medium">Active filters:</span>
              <div className="flex items-center space-x-2">
                {activeCategory !== 'all' && (
                  <span className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-brand-medium">
                    <span>{categories?.find(c => c?.id === activeCategory)?.name}</span>
                    <button
                      onClick={() => handleCategoryClick('all')}
                      className="hover:text-primary/70 transition-brand"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                {searchQuery && (
                  <span className="flex items-center space-x-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-xs font-brand-medium">
                    <span>"{searchQuery}"</span>
                    <button
                      onClick={clearSearch}
                      className="hover:text-accent/70 transition-brand"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    setActiveCategory('all');
                    setSearchQuery('');
                    onCategoryChange('all');
                    onSearchChange('');
                  }}
                  className="text-text-secondary hover:text-foreground"
                >
                  Clear all
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CategoryFilter;